import { useState, useRef, useCallback, useEffect } from "react";
import { getSpeechWsUrl } from "../store/apiActions";
import { tokenManager } from "../utils/utility";
import textToSpeech from "../utils/textToSpeech";
import useIsMobile from "./useIsMobile";

/** Words that mean the user wants to abort the current stream / TTS. */
export const STOP_KEYWORDS = [
    "stop",
    "cancel",
    "abort",
    "quit",
    "halt",
    "genie stop",
    "band karo",
    "thamba",
    "wait",
    "genie wait",
    "ruko",
    "genie",
    "genie please stop",
    "please stop genie",
    "stop genie",
    "wait genie",
    "genie pause",
    "pause genie",
    "hey genie",
    "ok genie",
    "okay genie",
    "genie band karo",
    "genie ruko",
    "genie thamba",
    "genie stop please",
    "please genie stop",
    "genie please",
    "please genie"
];

/** Words that mean the user wants to temporarily pause (treated same as stop for now). */
export const PAUSE_KEYWORDS = ["pause", "hold on", "wait"];

/**
 * useVoiceUtils — Custom hook for WebSocket-based Speech-to-Text.
 *
 * Uses a backend WebSocket endpoint that accepts raw PCM audio (16kHz, mono, Int16)
 * and emits { type: "partial" | "final", text: string } messages.
 *
 * Completely independent of useVoiceInput (Web Speech API).
 *
 * @param {Object}   options
 * @param {boolean}  options.enabled         - Feature flag; set false to disable entirely.
 * @param {string}   options.wsUrl           - WebSocket URL (overrides env default).
 * @param {function} options.onTranscript    - Called with full accumulated text on every update.
 * @param {function} options.onPartial       - Optional: called with partial text only.
 * @param {function} options.onFinal         - Optional: called when a final segment arrives.
 * @param {function} options.onError         - Optional: called on errors.
 * @param {function} options.onStatusChange  - Optional: called with status string changes.
 *
 * @returns {{
 *   isListening: boolean,
 *   startListening: function,
 *   stopListening: function,
 *   toggleListening: function,
 *   status: string,
 *   partialText: string,
 *   finalText: string,
 * }}
 */

/**
 * Normalize phonetically similar variations of the word "genie" (e.g., jennie, jenny, etc.)
 * to standard "genie" to handle speech-to-text misrecognition.
 */
const normalizeGenieVariations = (text) => {
    if (!text) return text;
    const regex = /\b(jennie|jeneii|jenny|jenene|geni|gini|jinny|jini|jeanie|jeenie|jeeny|jeannie|jeany|jeny|geny|jeani|jeeni|giny|jinie)\b/gi;
    return text.replace(regex, "genie");
};

/**
 * Scan the last ~20 words of a transcript for any known stop/pause keyword.
 * Returns the matched keyword (lowercase) or null.
 */
const detectKeyword = (text) => {
    if (!text) return null;
    const normalizedText = normalizeGenieVariations(text);
    const words = normalizedText.trim().toLowerCase().split(/\s+/);
    const tail = words.slice(-20).join(" ");
    for (const kw of [...STOP_KEYWORDS, ...PAUSE_KEYWORDS]) {
        const re = new RegExp(`\\b${kw}\\b`);
        if (re.test(tail)) return kw;
    }
    return null;
};

/**
 * Remove all stop/pause keyword words from a transcript string.
 * Used to keep command words out of the message box.
 */
const stripKeywords = (text) => {
    if (!text) return text;
    let result = normalizeGenieVariations(text);
    for (const kw of [...STOP_KEYWORDS, ...PAUSE_KEYWORDS]) {
        const re = new RegExp(`\\b${kw}\\b`, "gi");
        result = result.replace(re, "");
    }
    // Clean up extra whitespace left by removal
    return result.replace(/\s{2,}/g, " ").trim();
};

/**
 * Check if the transcribed segment matches or has high overlap with the assistant's own voice readout.
 * Used to prevent speakers' audio feedback from triggering auto-stop / barge-in.
 */
const isAssistantSelfTalk = (segment, speakingText) => {
    if (!segment || !speakingText) return false;

    const normalize = (text) => {
        return text
            .toLowerCase()
            .replace(/[^\p{L}\p{N}\s]/gu, "") // strip punctuation/special chars
            .replace(/\s+/g, " ")
            .trim();
    };

    const cleanSegment = normalize(segment);
    const cleanSpeaking = normalize(speakingText);

    if (!cleanSegment || !cleanSpeaking) return false;

    // 1. Direct substring check
    if (cleanSpeaking.includes(cleanSegment)) {
        return true;
    }

    // 2. Word-by-word overlap check
    const segmentWords = cleanSegment.split(" ");
    if (segmentWords.length === 0) return false;

    let matchCount = 0;
    segmentWords.forEach(word => {
        if (cleanSpeaking.includes(word)) {
            matchCount++;
        }
    });

    const overlapRatio = matchCount / segmentWords.length;
    return overlapRatio > 0.75;
};

const useVoiceUtils = ({
    enabled = true,
    wsUrl,
    onTranscript,
    onPartial,
    onFinal,
    onError,
    onStatusChange,
    /** Called with the matched keyword string when a stop/pause keyword is detected. */
    onKeyword,
    /**
     * When true, disables the short silence-detection timeout so the mic stays open
     * indefinitely while a response is generating (background keyword-detection mode).
     */
    backgroundMode = false,
    /** Auto-stop TTS and streaming if a keyword is detected */
    isVoiceStopFeature = false,
    /** Function to stop streaming if keyword is detected */
    handleStopStreaming,
    /** Enable noise gate filter to suppress background noise. */
    enableNoiseGate = true,
    /** Enable Biquad bandpass (highpass + lowpass) filtering. */
    enableBandpassFilter = true,
    /** Base noise gate threshold (rms). */
    noiseGateThreshold = 0.025,
    /** Noise gate threshold when assistant is speaking/reading. */
    speakingNoiseGateThreshold = 0.080,
    /**
     * If set (ms), fire onSilenceAutoSend after this many ms of silence and stop
     * listening. Fires before the normal 10s hard-stop timeout.
     * e.g. 3000 = auto-send after 3 seconds of silence.
     */
    autoSendDelayMs = null,
    /**
     * Called when silence auto-send fires (should trigger handleSendMessage).
     * NOTE: This hook passes the latest accumulated transcript as the first arg:
     *   onSilenceAutoSend(transcriptText)
     * This is backward-compatible: existing handlers that accept 0 args still work.
     */
    onSilenceAutoSend = null,
    /**
     * WebSocket auth handshake behavior:
     * - false (default): preserve legacy behavior for other apps (send token only if present)
     * - true: always send `{ token: <string|null> }` on WS open (DeepInsights voice overlay expects this)
     */
    sendNullTokenOnOpen = false,
} = {}) => {
    const [isListening, setIsListening] = useState(false);
    const [status, setStatus] = useState("idle"); // idle | connecting | connected | error | disconnected
    const [partialText, setPartialText] = useState("");
    const [finalText, setFinalText] = useState("");

    const isMobile = useIsMobile();

    // Refs — avoid stale closures in callbacks
    const websocketRef = useRef(null);
    const audioContextRef = useRef(null);
    const processorRef = useRef(null);
    const streamRef = useRef(null);
    const finalTranscriptsRef = useRef([]);
    /** Latest accumulated transcript (used for silence auto-send). */
    const latestTranscriptRef = useRef("");
    const isManualStopRef = useRef(false);
    /** Ref-based lock to prevent double startListening calls in the same render cycle. */
    const isStartingRef = useRef(false);
    const silenceTimeoutRef = useRef(null);
    /** Separate shorter timer that fires onSilenceAutoSend before the main stop timeout. */
    const autoSendTimerRef = useRef(null);
    /** Ref mirror of backgroundMode — avoids stale closure inside WS onmessage. */
    const backgroundModeRef = useRef(backgroundMode);
    /** Ref mirrors for auto-send so they're always current in callbacks. */
    const autoSendDelayMsRef = useRef(autoSendDelayMs);
    const onSilenceAutoSendRef = useRef(onSilenceAutoSend);

    // We need to call resetSilenceTimeout from an effect, so we use a ref
    const resetSilenceTimeoutRef = useRef(null);

    // When background mode is toggled on (e.g. going into loading state), instantly reset 
    // the silence timeout so it upgrades from 6 seconds to 10 minutes. Otherwise, the old 
    // 6-second timer will fire 3 seconds into the loading phase and kill the mic!
    useEffect(() => {
        backgroundModeRef.current = backgroundMode;
        if (backgroundMode && isListening) {
            resetSilenceTimeoutRef.current?.();
        }
    }, [backgroundMode, isListening]);


    /** Ref to startListening so ws.onclose can auto-reconnect in background mode. */
    const startListeningRef = useRef(null);

    // Keep auto-send refs in sync
    useEffect(() => { autoSendDelayMsRef.current = autoSendDelayMs; }, [autoSendDelayMs]);
    useEffect(() => { onSilenceAutoSendRef.current = onSilenceAutoSend; }, [onSilenceAutoSend]);

    // ── Helpers ─────────────────────────────────────────────────────────────

    const clearAutoSendTimer = useCallback(() => {
        if (autoSendTimerRef.current) {
            clearTimeout(autoSendTimerRef.current);
            autoSendTimerRef.current = null;
        }
    }, []);

    const clearSilenceTimeout = useCallback(() => {
        if (silenceTimeoutRef.current) {
            clearTimeout(silenceTimeoutRef.current);
            silenceTimeoutRef.current = null;
        }
    }, []);

    const updateStatus = useCallback((newStatus) => {
        setStatus(newStatus);
        onStatusChange?.(newStatus);
    }, [onStatusChange]);

    /** Convert Float32 PCM samples to Int16 ArrayBuffer (required by most STT backends) */
    const convertFloat32ToInt16 = useCallback((buffer) => {
        const len = buffer.length;
        const out = new Int16Array(len);
        for (let i = 0; i < len; i++) {
            const s = Math.max(-1, Math.min(1, buffer[i]));
            out[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
        }
        return out.buffer;
    }, []);

    /** Resolve the WebSocket URL using the API endpoint constant + Talent Pulse base URL. */
    const resolveWsUrl = useCallback(() => {
        if (wsUrl) return wsUrl;
        return getSpeechWsUrl();
    }, [wsUrl]);

    // ── Cleanup ─────────────────────────────────────────────────────────────

    const cleanupAudio = useCallback(() => {
        if (processorRef.current) {
            try { processorRef.current.disconnect(); } catch (_) { /* ignore */ }
            processorRef.current = null;
        }
        if (audioContextRef.current) {
            try { audioContextRef.current.close(); } catch (_) { /* ignore */ }
            audioContextRef.current = null;
        }
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
            streamRef.current = null;
        }
    }, []);

    const cleanupWebSocket = useCallback(() => {
        if (websocketRef.current) {
            try {
                // Only close if not already closed
                if (websocketRef.current.readyState !== WebSocket.CLOSED) {
                    websocketRef.current.close();
                }
            } catch (_) { /* ignore */ }
            websocketRef.current = null;
        }
    }, []);

    // ── Core — stop ─────────────────────────────────────────────────────────

    const stopListening = useCallback(() => {
        isManualStopRef.current = true;
        isStartingRef.current = false;
        backgroundModeRef.current = false;
        clearSilenceTimeout();
        clearAutoSendTimer();
        cleanupAudio();
        cleanupWebSocket();
        setIsListening(false);
        setPartialText("");
        latestTranscriptRef.current = "";
        updateStatus("idle");
    }, [clearSilenceTimeout, clearAutoSendTimer, cleanupAudio, cleanupWebSocket, updateStatus]);

    const resetSilenceTimeout = useCallback(() => {
        clearSilenceTimeout();
        clearAutoSendTimer();
        // Use ref so duration is always current even when backgroundMode changes
        // after the WebSocket connection was established (avoids stale closure).
        const timeoutMs = backgroundModeRef.current ? 600000 : (isMobile ? 10500 : 10000);
        silenceTimeoutRef.current = setTimeout(() => {
            stopListening();
        }, timeoutMs);

        // If auto-send is configured, arm a shorter timer that fires first
        if (autoSendDelayMsRef.current && onSilenceAutoSendRef.current && !backgroundModeRef.current) {
            autoSendTimerRef.current = setTimeout(() => {
                const cb = onSilenceAutoSendRef.current;
                const transcriptToSend = latestTranscriptRef.current;
                stopListening();
                if (cb) cb(transcriptToSend);
            }, autoSendDelayMsRef.current);
        }
    }, [clearSilenceTimeout, clearAutoSendTimer, stopListening, isMobile]);
    // backgroundModeRef/autoSendDelayMsRef/onSilenceAutoSendRef are refs — intentionally not in deps.
    resetSilenceTimeoutRef.current = resetSilenceTimeout;


    // ── Core — start ────────────────────────────────────────────────────────

    const startListening = useCallback(async () => {
        if (!enabled) return;
        // Guard: both React state AND ref-based lock to prevent double-start
        // within the same render cycle (state is stale until re-render).
        if (isListening || isStartingRef.current) {
            return;
        }
        isStartingRef.current = true;

        // Safety: close any orphaned WebSocket from a previous session
        cleanupWebSocket();

        // Reset state — clear old transcripts so keywords from previous turn don't re-fire
        isManualStopRef.current = false;
        finalTranscriptsRef.current = [];
        setPartialText("");
        setFinalText("");
        latestTranscriptRef.current = "";

        // 1. Request microphone access
        let micStream;
        try {
            micStream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    autoGainControl: true,
                    googEchoCancellation: true,
                    googNoiseSuppression: true,
                    googAutoGainControl: true,
                    googHighpassFilter: true,
                    googNoiseSuppression2: true,
                    googEchoCancellation2: true,
                    channelCount: 1,
                    sampleRate: 16000
                }
            });
        } catch (err) {
            const msg = "Microphone access denied";
            updateStatus("error");
            setIsListening(false);
            isStartingRef.current = false;
            onError?.(msg, err);
            return;
        }
        streamRef.current = micStream;

        // 2. Build WebSocket URL
        const token = tokenManager.getToken("token");
        const url = resolveWsUrl();

        // 3. Open WebSocket
        let ws;
        try {
            ws = new WebSocket(url);
        } catch (err) {
            cleanupAudio();
            const msg = "Failed to open WebSocket";
            updateStatus("error");
            setIsListening(false);
            isStartingRef.current = false;
            onError?.(msg, err);
            return;
        }
        ws.binaryType = "arraybuffer";
        websocketRef.current = ws;
        updateStatus("connecting");

        // 4. WebSocket event handlers
        ws.onopen = () => {
            if (isManualStopRef.current) {
                ws.close();
                return;
            }

            // Send token as the first message
            if (sendNullTokenOnOpen) {
                // DeepInsights STT can require an explicit first-frame auth envelope.
                ws.send(JSON.stringify({ token: token || null }));
            } else if (token) {
                // Legacy behavior (other apps): don't send anything when token is absent.
                ws.send(JSON.stringify({ token }));
            }

            updateStatus("connected");
            setIsListening(true);
            isStartingRef.current = false; // lock released — WS is now open
            resetSilenceTimeout();

            // 5. Set up audio pipeline at 16kHz mono
            const audioCtx = new AudioContext({ sampleRate: 16000 });
            audioContextRef.current = audioCtx;

            const source = audioCtx.createMediaStreamSource(micStream);

            // Set up filter chain
            let lastNode = source;
            if (enableBandpassFilter) {
                // Highpass filter (cuts off frequencies below 150Hz, e.g. AC rumbles, wind, background humming)
                const highpass = audioCtx.createBiquadFilter();
                highpass.type = "highpass";
                highpass.frequency.value = 150;
                lastNode.connect(highpass);
                lastNode = highpass;

                // Lowpass filter (cuts off frequencies above 7000Hz, e.g. high-frequency hiss, digital noise)
                const lowpass = audioCtx.createBiquadFilter();
                lowpass.type = "lowpass";
                lowpass.frequency.value = 7000;
                lastNode.connect(lowpass);
                lastNode = lowpass;
            }

            // ScriptProcessor is deprecated but has the widest support; bufferSize 4096
            const processor = audioCtx.createScriptProcessor(4096, 1, 1);
            processorRef.current = processor;

            lastNode.connect(processor);
            processor.connect(audioCtx.destination);

            processor.onaudioprocess = (event) => {
                const inputData = event.inputBuffer.getChannelData(0);

                // 1. Calculate Root Mean Square (RMS) volume of the current audio chunk
                let sum = 0;
                for (let i = 0; i < inputData.length; i++) {
                    sum += inputData[i] * inputData[i];
                }
                const rms = Math.sqrt(sum / inputData.length);

                // 2. Detect if the assistant is currently speaking/reading out an answer
                const isTtsSpeaking = typeof window !== "undefined" && window.speechSynthesis && window.speechSynthesis.speaking;
                const isStreamingVopPlaying = typeof window !== "undefined" && (!!window.isStreamingVopPlaying || !!window.isAzureVopPlaying);
                const hasSpeakingText = typeof window !== "undefined" && !!window.currentSpeakingText;
                const isSpeaking = isTtsSpeaking || isStreamingVopPlaying || hasSpeakingText;

                // 3. Determine the threshold: use a higher threshold if the bot is reading out,
                // to suppress the speaker's echo/feedback, while still allowing the user to barge in by speaking louder.
                const currentThreshold = isSpeaking ? speakingNoiseGateThreshold : noiseGateThreshold;

                if (enableNoiseGate) {
                    // 4. If the volume is below the threshold, silence the buffer to prevent background voice/noise from being sent
                    if (rms < currentThreshold) {
                        for (let i = 0; i < inputData.length; i++) {
                            inputData[i] = 0.0;
                        }
                    } else {
                        // 5. Voice activity detected above gate — reset the silence timer so
                        //    continuous speech never triggers the auto-stop mid-sentence.
                        //    Use the ref so we always call the latest version (no stale closure).
                        resetSilenceTimeoutRef.current?.();
                    }
                } else {
                    // Noise gate off — reset silence timer on any audio above a minimal floor
                    if (rms > 0.005) {
                        resetSilenceTimeoutRef.current?.();
                    }
                }

                const pcm = convertFloat32ToInt16(inputData);
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(pcm);
                }
            };
        };

        ws.onmessage = (event) => {
            let data;
            try {
                data = typeof event.data === "string"
                    ? JSON.parse(event.data)
                    : null;
            } catch {
                return;
            }
            if (!data) return;

            resetSilenceTimeout();

            if (data.type === "partial") {
                const partial = data.text || "";
                setPartialText(partial);
                onPartial?.(partial);

                const accumulated = [
                    ...finalTranscriptsRef.current,
                    partial,
                ].join(" ").trim();
                latestTranscriptRef.current = accumulated;

                // Keyword detection — check rolling window
                const kw = detectKeyword(accumulated);
                if (kw) {
                    if (isVoiceStopFeature) {
                        onTranscript?.("");
                        finalTranscriptsRef.current = [];
                        setPartialText("");
                        setFinalText("");
                        latestTranscriptRef.current = "";
                        handleStopStreaming?.();
                        window.dispatchEvent(new CustomEvent("voiceStopKeyword"));
                        textToSpeech.stop();
                    } else {
                        const clean = backgroundModeRef.current ? "" : stripKeywords(accumulated);
                        latestTranscriptRef.current = clean;
                        onTranscript?.(clean);
                    }
                    onKeyword?.(kw);
                } else {
                    if (!backgroundModeRef.current) {
                        latestTranscriptRef.current = accumulated;
                        onTranscript?.(accumulated);
                    }
                }

            } else if (data.type === "final") {
                const segment = data.text || "";
                if (segment) {
                    finalTranscriptsRef.current.push(segment);
                }
                setPartialText("");
                setFinalText(finalTranscriptsRef.current.join(" ").trim());
                onFinal?.(segment);

                const accumulated = finalTranscriptsRef.current.join(" ").trim();
                latestTranscriptRef.current = accumulated;

                // Keyword detection on final segment too
                const kw = detectKeyword(accumulated);
                if (kw) {
                    const remainder = stripKeywords(accumulated);
                    const wordCount = accumulated.trim().split(/\s+/).length;
                    const hasFollowUp = wordCount > 3 && !!remainder;
                    const inputText = hasFollowUp ? remainder : "";
                    if (isVoiceStopFeature) {
                        onTranscript?.(inputText);
                        finalTranscriptsRef.current = [];
                        setPartialText("");
                        setFinalText("");
                        latestTranscriptRef.current = inputText;
                        handleStopStreaming?.();
                        window.dispatchEvent(new CustomEvent("voiceStopKeyword"));
                        textToSpeech.stop();
                        if (hasFollowUp) {
                            window.dispatchEvent(new CustomEvent("voiceStopWithQuery", { detail: { query: remainder } }));
                        }
                    } else {
                        const clean = backgroundModeRef.current ? inputText : stripKeywords(accumulated);
                        latestTranscriptRef.current = clean;
                        onTranscript?.(clean);
                    }
                    onKeyword?.(kw);
                } else {
                    // In background mode (TTS is playing), if the user speaks more than 2 words
                    // without a stop keyword, treat it as a stop + new query.
                    // This is currently disabled (marked as false). Set to true in the future to enable.
                    const enableAutoStopWithoutKeyword = false;

                    const isSelfTalk = isAssistantSelfTalk(segment, typeof window !== "undefined" ? window.currentSpeakingText : "");
                    if (enableAutoStopWithoutKeyword && backgroundModeRef.current && segment.trim().split(/\s+/).length > 2 && !isSelfTalk) {
                        onTranscript?.(segment);
                        finalTranscriptsRef.current = [];
                        setPartialText("");
                        setFinalText("");
                        latestTranscriptRef.current = segment;
                        handleStopStreaming?.();
                        window.dispatchEvent(new CustomEvent("voiceStopKeyword"));
                        textToSpeech.stop();
                        window.dispatchEvent(new CustomEvent("voiceStopWithQuery", { detail: { query: segment } }));
                        onKeyword?.("__auto_stop__");
                    } else if (!backgroundModeRef.current) {
                        latestTranscriptRef.current = accumulated;
                        onTranscript?.(accumulated);
                    }
                }
            }
        };

        ws.onclose = (evt) => {
            clearSilenceTimeout();
            cleanupAudio();
            setIsListening(false);

            if (isManualStopRef.current) {
                updateStatus("idle");
                return;
            }

            // In background mode (during streaming/readout), auto-reconnect so the
            // mic stays open for keyword detection ("stop") throughout the response.
            if (backgroundModeRef.current) {
                updateStatus("connecting");
                setTimeout(() => {
                    if (backgroundModeRef.current && !isManualStopRef.current) {
                        startListeningRef.current?.();
                    }
                }, 600);
                return;
            }

            // Non-manual close — surface an error status
            if (evt.code === 4001) {
                const msg = "Auth failed (4001) — check your token";
                updateStatus("error");
                onError?.(msg, evt);
            } else if (evt.code === 1011) {
                const msg = "Server error (1011) — backend not configured";
                updateStatus("error");
                onError?.(msg, evt);
            } else {
                updateStatus("disconnected");
            }
        };

        ws.onerror = (err) => {
            clearSilenceTimeout();
            cleanupAudio();
            cleanupWebSocket();
            setIsListening(false);
            const msg = "WebSocket error — is the backend running?";
            updateStatus("error");
            onError?.(msg, err);
        };
    }, [
        enabled,
        isListening,
        resolveWsUrl,
        convertFloat32ToInt16,
        cleanupAudio,
        cleanupWebSocket,
        updateStatus,
        onTranscript,
        onPartial,
        onFinal,
        onError,
        onKeyword,
        isVoiceStopFeature,
        handleStopStreaming,
        resetSilenceTimeout,
        clearSilenceTimeout,
        sendNullTokenOnOpen,
        enableNoiseGate,
        enableBandpassFilter,
        noiseGateThreshold,
        speakingNoiseGateThreshold,
    ]);

    // Keep ref pointing to latest startListening so ws.onclose can auto-reconnect
    startListeningRef.current = startListening;

    // ── Toggle convenience ───────────────────────────────────────────────────

    const toggleListening = useCallback(() => {
        if (isListening) {
            stopListening();
        } else {
            startListening();
        }
    }, [isListening, startListening, stopListening]);

    // ── Cleanup on unmount ───────────────────────────────────────────────────

    useEffect(() => {
        return () => {
            isManualStopRef.current = true;
            cleanupAudio();
            cleanupWebSocket();
        };
    }, [cleanupAudio, cleanupWebSocket]);

    return {
        isListening,
        startListening,
        stopListening,
        toggleListening,
        status,
        partialText,
        finalText,
    };
};

export default useVoiceUtils;
